import React, { useEffect, useState } from 'react';
import { Box, Button, Stack, Typography, alpha } from '@mui/material';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import { useSnackbar } from 'notistack';
import AddressAutocomplete from '../../../../Components/AddressAutocomplete';
import { useAppContext, useFeature } from '../../../../Context/AppContext';

const JobHeroSite = ({ job, onSave }) => {
	const { state } = useAppContext();
	const { enqueueSnackbar } = useSnackbar();
	const hasScheduling = useFeature('scheduling');
	const hasTimeTracking = useFeature('time_tracking');
	const canUseSite = hasScheduling || hasTimeTracking;
	const isManager =
		state.user.role === 'Manager' || Boolean(state.user.isSuperuser);

	const [editing, setEditing] = useState(false);
	const [siteAddress, setSiteAddress] = useState(job?.site_address || '');
	const [latitude, setLatitude] = useState(
		job?.site_latitude != null ? String(job.site_latitude) : '',
	);
	const [longitude, setLongitude] = useState(
		job?.site_longitude != null ? String(job.site_longitude) : '',
	);
	const [saving, setSaving] = useState(false);

	useEffect(() => {
		if (editing) return;
		setSiteAddress(job?.site_address || '');
		setLatitude(job?.site_latitude != null ? String(job.site_latitude) : '');
		setLongitude(job?.site_longitude != null ? String(job.site_longitude) : '');
	}, [job?.site_address, job?.site_latitude, job?.site_longitude, editing]);

	if (!canUseSite) {
		return null;
	}

	const handleCancel = () => {
		setSiteAddress(job?.site_address || '');
		setLatitude(job?.site_latitude != null ? String(job.site_latitude) : '');
		setLongitude(job?.site_longitude != null ? String(job.site_longitude) : '');
		setEditing(false);
	};

	const handleSave = async () => {
		if (!isManager || !onSave) return;
		setSaving(true);
		try {
			const lat = latitude.trim() === '' ? null : Number.parseFloat(latitude);
			const lng = longitude.trim() === '' ? null : Number.parseFloat(longitude);
			if (
				(latitude.trim() !== '' && Number.isNaN(lat)) ||
				(longitude.trim() !== '' && Number.isNaN(lng))
			) {
				enqueueSnackbar('Latitude and longitude must be numbers.', {
					variant: 'warning',
				});
				return;
			}
			await onSave({
				site_address: siteAddress,
				site_latitude: lat,
				site_longitude: lng,
			});
			enqueueSnackbar('Job site updated.', { variant: 'success' });
			setEditing(false);
		} catch {
			// parent surfaces API errors
		} finally {
			setSaving(false);
		}
	};

	const summary = siteAddress?.trim() ? siteAddress.trim() : 'Add job site';

	const shellSx = {
		width: '100%',
		px: 1.25,
		py: 1,
		borderRadius: 2,
		border: '1px solid',
		borderColor: (t) =>
			editing
				? alpha(t.palette.primary.main, 0.45)
				: t.palette.mode === 'dark'
					? alpha(t.palette.common.white, 0.12)
					: alpha(t.palette.common.black, 0.1),
		bgcolor: (t) =>
			editing
				? alpha(t.palette.primary.main, 0.08)
				: t.palette.mode === 'dark'
					? alpha(t.palette.common.white, 0.03)
					: alpha(t.palette.common.black, 0.02),
	};

	if (editing && isManager) {
		return (
			<Box sx={shellSx}>
				<Stack
					direction="row"
					alignItems="flex-start"
					spacing={1}
					sx={{ mb: 0.5 }}
				>
					<PlaceOutlinedIcon
						sx={{
							fontSize: 18,
							color: 'primary.main',
							flexShrink: 0,
							mt: 1.25,
						}}
					/>
					<Box sx={{ minWidth: 0, flex: 1 }}>
						<Typography
							variant="caption"
							sx={{
								display: 'block',
								fontWeight: 700,
								letterSpacing: 0.5,
								textTransform: 'uppercase',
								color: 'text.secondary',
								lineHeight: 1.2,
								fontSize: '0.65rem',
								mb: 0.5,
							}}
						>
							Site
						</Typography>
						<AddressAutocomplete
							label=""
							dense
							autoFocus
							value={siteAddress}
							latitude={latitude}
							longitude={longitude}
							onChange={({ address, latitude: lat, longitude: lng }) => {
								setSiteAddress(address);
								setLatitude(lat);
								setLongitude(lng);
							}}
							textFieldProps={{
								placeholder: 'Search address…',
								variant: 'standard',
								InputProps: {
									disableUnderline: true,
								},
								sx: {
									'& .MuiInputBase-root': {
										fontWeight: 600,
										fontSize: '0.875rem',
									},
									'& .MuiFormHelperText-root': {
										mx: 0,
										mt: 0.5,
									},
								},
							}}
						/>
					</Box>
				</Stack>
				<Stack direction="row" spacing={1} sx={{ pl: 3.5, pt: 0.5 }}>
					<Button
						variant="contained"
						size="small"
						onClick={handleSave}
						disabled={saving}
					>
						{saving ? 'Saving…' : 'Save'}
					</Button>
					<Button
						variant="text"
						size="small"
						onClick={handleCancel}
						disabled={saving}
					>
						Cancel
					</Button>
				</Stack>
			</Box>
		);
	}

	return (
		<Box
			component="button"
			type="button"
			onClick={() => isManager && setEditing(true)}
			disabled={!isManager}
			sx={{
				...shellSx,
				display: 'flex',
				alignItems: 'center',
				gap: 1,
				textAlign: 'left',
				cursor: isManager ? 'pointer' : 'default',
				fontFamily: 'inherit',
				color: 'inherit',
			}}
		>
			<PlaceOutlinedIcon
				sx={{ fontSize: 18, color: 'primary.main', flexShrink: 0 }}
			/>
			<Box sx={{ minWidth: 0, flex: 1 }}>
				<Typography
					variant="caption"
					sx={{
						display: 'block',
						fontWeight: 700,
						letterSpacing: 0.5,
						textTransform: 'uppercase',
						color: 'text.secondary',
						lineHeight: 1.2,
						fontSize: '0.65rem',
					}}
				>
					Site
				</Typography>
				<Typography variant="body2" noWrap fontWeight={600}>
					{summary}
				</Typography>
			</Box>
		</Box>
	);
};

export default JobHeroSite;
